import { Chart, registerables } from "chart.js";
import { Stats } from "./Stats";

Chart.register(...registerables);

export type StatsType = {
  timestamp: number;
  bitrate?: number;
  bytesSent?: number;
  bytesReceived?: number;
};

export class StatsChartService {
  private chart: Chart<"line", string[], string>;

  constructor(
    element: HTMLCanvasElement,
    label: string,
    initialData: StatsType[] = []
  ) {
    this.chart = this.createChart(element, label, initialData);
  }

  public tick(data: StatsType) {
    this.chart.data.labels?.push(this.getTime(data.timestamp));
    this.chart.data.labels = this.scrollData(this.chart.data.labels || []);
    if (data.bitrate) {
      this.chart.data.datasets[0].data.push(data.bitrate.toFixed(2));
    }

    if (data.bytesSent) {
      this.chart.data.datasets[0].data.push(data.bytesSent.toFixed(2));
    }

    if (data.bytesReceived) {
      this.chart.data.datasets[0].data.push(data.bytesReceived.toFixed(2));
    }
    this.chart.data.datasets[0].data = this.scrollData(
      this.chart.data.datasets[0].data
    );
    this.chart.update();
  }

  public destroy() {
    this.chart.destroy();
  }

  private createChart(
    element: HTMLCanvasElement,
    label: string,
    initialData: StatsType[] = []
  ): Chart<"line", string[], string> {
    const chart = new Chart<"line", string[], string>(element, {
      type: "line",
      data: {
        labels: this.getInitialDataLabels(initialData),
        datasets: [
          {
            label,
            data: this.getInitialData(initialData),
            backgroundColor: "rgba(0, 0, 0, 0)",
            borderColor: "rgba(255, 0, 0, 1)",
            borderWidth: 1,
            pointRadius: 0
          }
        ]
      },
      options: {
        animation: false,
        scales: {
          y: {
            ticks: {
              callback: (val: string | number) =>
                Stats.humanFileSize(Math.abs(parseInt(val as string)))
            }
          }
        }
      }
    });
    const chartNode = chart.canvas.parentNode as HTMLElement;
    chartNode.style.width = "400px";
    return chart;
  }

  private getInitialDataLabels(data: StatsType[]): string[] {
    const labels = data.map((d) => this.getTime(d.timestamp));
    return this.scrollData(labels);
  }

  private getInitialData(data: StatsType[]): string[] {
    if (data.length === 0) {
      return [];
    }
    if (data[0].bitrate) {
      return this.scrollData(data.map((d) => d.bitrate?.toFixed(2) || ""));
    }
    if (data[0].bytesSent) {
      return this.scrollData(data.map((d) => d.bytesSent?.toFixed(2) || ""));
    }
    if (data[0].bytesReceived) {
      return this.scrollData(
        data.map((d) => d.bytesReceived?.toFixed(2) || "")
      );
    }
    return [];
  }

  private getTime(timestamp: number): string {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    return hours + ":" + minutes + ":" + seconds;
  }

  private scrollData(data: string[]): string[] {
    if (data.length > 6) {
      const newData = [...data];
      newData.shift();
      return [...newData];
    }
    return [...data];
  }

  public static create(
    element: HTMLCanvasElement,
    label: string,
    initialData: StatsType[] = []
  ): StatsChartService {
    return new StatsChartService(element, label, initialData);
  }
}
